import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Mail, Phone, Globe, MessageCircle, MapPin, Tag, Search, X, Facebook, Instagram, Linkedin, Calendar, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUrl';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUSINESS_CATEGORIES = [
  "All Categories", "Children", "Home & Garden", "Pets",
  "Professional Services", "Wellness & Beauty", "Women's Lifestyle"
];

export default function DirectoryPage({ user }) {
  const [areas, setAreas] = useState(["All Areas"]);
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedArea, setSelectedArea] = useState('All Areas');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedMember, setSelectedMember] = useState(null);
  const [attendingIds, setAttendingIds] = useState(new Set());
  const [memberEvents, setMemberEvents] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('business'); // 'business' or 'member'

  useEffect(() => {
    axios.get(`${API}/areas`).then(res => setAreas(["All Areas", ...res.data])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchMembers();
    fetchAttendingIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea]);

  useEffect(() => {
    if (selectedMember) {
      fetchMemberEvents(selectedMember.id);
    } else {
      setMemberEvents([]);
    }
  }, [selectedMember]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const memberId = searchParams.get('member');
    if (memberId && members.length > 0) {
      const found = members.find(m => m.id === memberId);
      if (found) {
        setSelectedMember(found);
        setSearchParams({}, { replace: true });
      }
    }
  }, [members, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMembers = async () => {
    try {
      const params = selectedArea !== 'All Areas' ? { area: selectedArea } : {};
      const response = await axios.get(`${API}/users`, { params });
      const sortedMembers = response.data.sort((a, b) =>
        (a.full_name || '').localeCompare(b.full_name || '')
      );
      setMembers(sortedMembers);
    } catch (error) {
      toast.error('Failed to load members');
    }
  };

  const fetchAttendingIds = async () => {
    try {
      const response = await axios.get(`${API}/events/attending-user-ids`);
      setAttendingIds(new Set(response.data.attending_user_ids || []));
    } catch (error) { /* non-critical */ }
  };

  const fetchMemberEvents = async (memberId) => {
    try {
      const response = await axios.get(`${API}/users/${memberId}/upcoming-events`);
      setMemberEvents(response.data || []);
    } catch (error) { setMemberEvents([]); }
  };

  const handleSearch = () => { setSearchTerm(searchInput); };
  const handleKeyPress = (e) => { if (e.key === 'Enter') handleSearch(); };

  // Flatten members into business cards
  const buildBusinessCards = () => {
    const cards = [];
    for (const member of members) {
      const displayName = member.display_name || member.full_name;

      // Primary business card
      cards.push({
        key: `${member.id}_primary`,
        memberId: member.id,
        member,
        isPrimary: true,
        businessName: member.business_name || displayName,
        businessDescription: member.business_description || '',
        businessCategory: member.business_category || '',
        logo: member.business_logo || null,
        profileImage: member.profile_image || null,
        email: member.display_email || member.email,
        phone: member.phone || '',
        website: member.website || '',
        facebook: member.facebook || '',
        instagram: member.instagram || '',
        linkedin: member.linkedin || '',
        tiktok: member.tiktok || '',
        tags: member.tags || [],
        area: member.area || '',
        ownerName: displayName,
        isAttending: attendingIds.has(member.id)
      });

      // Additional businesses
      if (member.businesses && member.businesses.length > 0) {
        for (const biz of member.businesses) {
          cards.push({
            key: `${member.id}_${biz.id}`,
            memberId: member.id,
            member,
            isPrimary: false,
            businessName: biz.name || 'Unnamed Business',
            businessDescription: biz.description || '',
            businessCategory: biz.category || '',
            logo: biz.logo || null,
            profileImage: member.profile_image || null,
            email: biz.email || member.display_email || member.email,
            phone: biz.phone || member.phone || '',
            website: biz.website || '',
            facebook: biz.facebook || '',
            instagram: biz.instagram || '',
            linkedin: biz.linkedin || '',
            tiktok: biz.tiktok || '',
            tags: biz.tags || [],
            area: member.area || '',
            ownerName: displayName,
            isAttending: attendingIds.has(member.id)
          });
        }
      }
    }
    return cards;
  };

  const allCards = buildBusinessCards();

  // Filter cards
  const filteredCards = allCards.filter((card) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      card.businessName.toLowerCase().includes(searchLower) ||
      card.ownerName.toLowerCase().includes(searchLower) ||
      card.email.toLowerCase().includes(searchLower) ||
      card.businessDescription.toLowerCase().includes(searchLower) ||
      card.businessCategory.toLowerCase().includes(searchLower) ||
      card.area.toLowerCase().includes(searchLower) ||
      card.tags.some(tag => tag.toLowerCase().includes(searchLower));

    const matchesCategory = selectedCategory === 'All Categories' ||
      card.businessCategory === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Sort based on view mode
  filteredCards.sort((a, b) => 
    viewMode === 'business' 
      ? a.businessName.localeCompare(b.businessName)
      : a.ownerName.localeCompare(b.ownerName)
  );

  return (
    <div className="max-w-7xl mx-auto px-6" data-testid="directory-page">
      <h1 className="text-4xl font-bold text-[#2d3748] mb-2">Directory</h1>
      <p className="text-[#64748b] mb-8">{filteredCards.length} listing{filteredCards.length !== 1 ? 's' : ''}</p>

      {/* Member Profile Dialog */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="max-w-lg bg-white p-0 overflow-hidden">
          <DialogTitle className="sr-only">Member Profile</DialogTitle>
          {selectedMember && (
            <>
              <button onClick={() => setSelectedMember(null)}
                className="absolute top-4 right-4 z-10 p-2 bg-white/80 hover:bg-white rounded-full shadow-md">
                <X className="w-5 h-5 text-gray-600" />
              </button>

              <div className="relative">
                <div className="h-48 bg-gradient-to-br from-[#5b7f95] to-[#4a6b80] overflow-hidden">
                  {selectedMember.cover_image && (
                    <img src={getImageUrl(selectedMember.cover_image)} alt="Cover" className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="absolute -bottom-16 left-1/2 -translate-x-1/2">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-100">
                    {selectedMember.profile_image ? (
                      <img src={getImageUrl(selectedMember.profile_image)}
                        alt={selectedMember.display_name || selectedMember.full_name}
                        className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#5b7f95] text-white text-4xl font-semibold">
                        {(selectedMember.display_name || selectedMember.full_name).charAt(0)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-20 pb-6 px-6 text-center">
                <h2 className="text-2xl font-bold text-[#2d3748]">
                  {selectedMember.display_name || selectedMember.full_name}
                </h2>
                {selectedMember.business_name && (
                  <p className="text-[#5b7f95] font-medium mt-1">{selectedMember.business_name}</p>
                )}

                <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
                  {selectedMember.business_category && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#5b7f95]/10 text-[#5b7f95] text-sm font-medium rounded-full">
                      <Tag className="w-3 h-3" />{selectedMember.business_category}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                    <MapPin className="w-3 h-3" />{selectedMember.area}
                  </span>
                </div>

                {selectedMember.business_description && (
                  <p className="text-[#64748b] mt-4 text-sm leading-relaxed">{selectedMember.business_description}</p>
                )}

                <div className="mt-6 space-y-2 text-sm">
                  <div className="flex items-center justify-center gap-2 text-[#64748b]">
                    <Mail className="w-4 h-4 text-[#5b7f95]" />
                    <a href={`mailto:${selectedMember.display_email || selectedMember.email}`} className="hover:text-[#5b7f95]">
                      {selectedMember.display_email || selectedMember.email}
                    </a>
                  </div>
                  {selectedMember.phone && (
                    <div className="flex items-center justify-center gap-2 text-[#64748b]">
                      <Phone className="w-4 h-4 text-[#5b7f95]" />
                      <a href={`tel:${selectedMember.phone}`} className="hover:text-[#5b7f95]">{selectedMember.phone}</a>
                    </div>
                  )}
                  {selectedMember.website && (
                    <div className="flex items-center justify-center gap-2 text-[#64748b]">
                      <Globe className="w-4 h-4 text-[#5b7f95]" />
                      <a href={selectedMember.website.startsWith('http') ? selectedMember.website : `https://${selectedMember.website}`}
                        target="_blank" rel="noopener noreferrer" className="hover:text-[#5b7f95]">
                        {selectedMember.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                </div>

                {(selectedMember.facebook || selectedMember.instagram || selectedMember.linkedin) && (
                  <div className="flex items-center justify-center gap-4 mt-4">
                    {selectedMember.facebook && (
                      <a href={selectedMember.facebook.startsWith('http') ? selectedMember.facebook : `https://facebook.com/${selectedMember.facebook}`}
                        target="_blank" rel="noopener noreferrer" className="text-[#5b7f95] hover:text-[#4a6b80]">
                        <Facebook className="w-5 h-5" />
                      </a>
                    )}
                    {selectedMember.instagram && (
                      <a href={selectedMember.instagram.startsWith('http') ? selectedMember.instagram : `https://instagram.com/${selectedMember.instagram}`}
                        target="_blank" rel="noopener noreferrer" className="text-[#5b7f95] hover:text-[#4a6b80]">
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                    {selectedMember.linkedin && (
                      <a href={selectedMember.linkedin.startsWith('http') ? selectedMember.linkedin : `https://linkedin.com/in/${selectedMember.linkedin}`}
                        target="_blank" rel="noopener noreferrer" className="text-[#5b7f95] hover:text-[#4a6b80]">
                        <Linkedin className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                )}

                {selectedMember.tags && selectedMember.tags.length > 0 && (
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                    {selectedMember.tags.map((tag, idx) => (
                      <span key={`${tag}-${idx}`} className="px-3 py-1 bg-[#cb9944]/10 text-[#cb9944] text-xs font-medium rounded-full">{tag}</span>
                    ))}
                  </div>
                )}

                {/* Additional Businesses in profile dialog */}
                {selectedMember.businesses && selectedMember.businesses.length > 0 && (
                  <div className="mt-6 text-left border-t pt-4">
                    <h3 className="text-sm font-semibold text-[#2d3748] mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-[#5b7f95]" />
                      Other Businesses ({selectedMember.businesses.length})
                    </h3>
                    <div className="space-y-3">
                      {selectedMember.businesses.map((biz) => (
                        <div key={biz.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                          {biz.logo ? (
                            <img src={getImageUrl(biz.logo)} alt={biz.name} className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-[#5b7f95]/10 flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-[#5b7f95]" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-[#2d3748] truncate">{biz.name}</p>
                            {biz.category && <p className="text-xs text-[#5b7f95]">{biz.category}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {memberEvents.length > 0 && (
                  <div className="mt-6 text-left" data-testid="member-upcoming-events">
                    <h3 className="text-sm font-semibold text-[#2d3748] mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#5b7f95]" />Upcoming Events
                    </h3>
                    <div className="space-y-2">
                      {memberEvents.map((evt) => (
                        <Link key={evt.id} to={`/events/${evt.id}`} onClick={() => setSelectedMember(null)}
                          className="block p-3 bg-[#f0f5f8] rounded-xl hover:bg-[#e4edf2] transition-colors">
                          <p className="text-sm font-medium text-[#2d3748]">{evt.title}</p>
                          <p className="text-xs text-[#64748b] mt-1">
                            {format(new Date(evt.date), 'PPP p')} &bull; {evt.location}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {selectedMember.id !== user.id && (
                  <Link to={`/messages/${selectedMember.id}`} onClick={() => setSelectedMember(null)}>
                    <Button className="w-full mt-6 bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full">
                      <MessageCircle className="w-4 h-4 mr-2" />Send Message
                    </Button>
                  </Link>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex gap-2">
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress} placeholder="Search by name, business, category, tags..."
            className="flex-1 bg-white border-gray-200 rounded-xl" data-testid="search-members-input" />
          <Button onClick={handleSearch} className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-xl px-6" data-testid="search-btn">
            <Search className="w-4 h-4 mr-2" />Search
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-full sm:w-56 bg-white border-gray-200 rounded-xl" data-testid="view-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="business">Sort by Business Name</SelectItem>
              <SelectItem value="member">Sort by Member Name</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-56 bg-white border-gray-200 rounded-xl" data-testid="category-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BUSINESS_CATEGORIES.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={selectedArea} onValueChange={setSelectedArea}>
            <SelectTrigger className="w-full sm:w-56 bg-white border-gray-200 rounded-xl" data-testid="area-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {areas.map((area) => (<SelectItem key={area} value={area}>{area}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Business Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCards.map((card) => (
          <Card key={card.key} className="border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-all" data-testid="business-card">
            <CardHeader className="pb-2">
              <div className="flex items-start gap-4">
                {/* Image: profile pic in member mode, logo in business mode */}
                <button onClick={() => setSelectedMember(card.member)}
                  className={`relative h-16 w-16 overflow-hidden bg-gray-100 flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-[#5b7f95] transition-all ${viewMode === 'member' ? 'rounded-full' : 'rounded-xl'}`}>
                  {viewMode === 'member' ? (
                    // Member mode: show profile pic (round style)
                    card.profileImage ? (
                      <img src={getImageUrl(card.profileImage)} alt={card.ownerName}
                        className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#cb9944] text-white text-xl font-semibold">
                        {card.ownerName.charAt(0)}
                      </div>
                    )
                  ) : (
                    // Business mode: show logo
                    card.logo ? (
                      <img src={getImageUrl(card.logo)} alt={card.businessName}
                        className="absolute inset-0 w-full h-full object-cover" />
                    ) : card.profileImage ? (
                      <img src={getImageUrl(card.profileImage)} alt={card.ownerName}
                        className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#5b7f95] text-white text-xl font-semibold">
                        {card.businessName.charAt(0)}
                      </div>
                    )
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  {viewMode === 'member' ? (
                    <>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg text-[#2d3748] truncate">{card.ownerName}</h3>
                        {card.isAttending && (
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#5b7f95] text-white text-xs font-bold flex items-center justify-center"
                            title="Attending upcoming meeting" data-testid="meeting-badge">M</span>
                        )}
                      </div>
                      {card.businessName && card.businessName !== card.ownerName && (
                        <p className="text-sm text-[#5b7f95] truncate">{card.businessName}</p>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg text-[#2d3748] truncate">{card.businessName}</h3>
                        {card.isAttending && (
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#5b7f95] text-white text-xs font-bold flex items-center justify-center"
                            title="Attending upcoming meeting" data-testid="meeting-badge">M</span>
                        )}
                      </div>
                      <button onClick={() => setSelectedMember(card.member)}
                        className="text-sm text-[#5b7f95] hover:text-[#4a6b80] transition-colors cursor-pointer truncate block">
                        by {card.ownerName}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              {/* Chapter/Area */}
              {card.area && (
                <div className="flex items-center gap-2 text-sm text-[#64748b]">
                  <MapPin className="w-4 h-4 text-[#5b7f95] flex-shrink-0" /><span>{card.area}</span>
                </div>
              )}

              {/* Phone - always show */}
              <div className="flex items-center gap-2 text-sm text-[#64748b]">
                <Phone className="w-4 h-4 text-[#5b7f95] flex-shrink-0" />
                {card.phone ? (
                  <a href={`tel:${card.phone}`} className="hover:text-[#5b7f95]">{card.phone}</a>
                ) : (
                  <span className="text-gray-400 italic">No phone listed</span>
                )}
              </div>

              {/* Email */}
              <div className="flex items-center gap-2 text-sm text-[#64748b]">
                <Mail className="w-4 h-4 text-[#5b7f95] flex-shrink-0" />
                <a href={`mailto:${card.email}`} className="hover:text-[#5b7f95] truncate">{card.email}</a>
              </div>

              {/* Category Badge */}
              {card.businessCategory && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#5b7f95]/10 text-[#5b7f95] text-xs font-medium rounded-full">
                  <Tag className="w-3 h-3" />{card.businessCategory}
                </span>
              )}

              {/* Tags */}
              {card.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {card.tags.map((tag, idx) => (
                    <span key={`${tag}-${idx}`} className="px-2 py-0.5 bg-[#cb9944]/10 text-[#cb9944] text-xs font-medium rounded-full">{tag}</span>
                  ))}
                </div>
              )}

              {/* Description */}
              {card.businessDescription && (
                <p className="text-sm text-[#64748b] line-clamp-2">{card.businessDescription}</p>
              )}

              {/* Website */}
              {card.website && (
                <div className="flex items-center gap-2 text-sm text-[#64748b]">
                  <Globe className="w-4 h-4 text-[#5b7f95] flex-shrink-0" />
                  <a href={card.website.startsWith('http') ? card.website : `https://${card.website}`}
                    target="_blank" rel="noopener noreferrer" className="hover:text-[#5b7f95] truncate">
                    {card.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}

              {/* Social Links */}
              {(card.facebook || card.instagram || card.linkedin || card.tiktok) && (
                <div className="flex items-center gap-3 pt-1">
                  {card.facebook && (
                    <a href={card.facebook.startsWith('http') ? card.facebook : `https://facebook.com/${card.facebook}`}
                      target="_blank" rel="noopener noreferrer" className="text-[#5b7f95] hover:text-[#4a6b80]">
                      <Facebook className="w-4 h-4" />
                    </a>
                  )}
                  {card.instagram && (
                    <a href={card.instagram.startsWith('http') ? card.instagram : `https://instagram.com/${card.instagram}`}
                      target="_blank" rel="noopener noreferrer" className="text-[#5b7f95] hover:text-[#4a6b80]">
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                  {card.linkedin && (
                    <a href={card.linkedin.startsWith('http') ? card.linkedin : `https://linkedin.com/in/${card.linkedin}`}
                      target="_blank" rel="noopener noreferrer" className="text-[#5b7f95] hover:text-[#4a6b80]">
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                </div>
              )}

              {/* Message Button */}
              {card.memberId !== user.id && (
                <Link to={`/messages/${card.memberId}`}>
                  <Button size="sm" className="w-full mt-4 bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full" data-testid="send-message-btn">
                    <MessageCircle className="w-4 h-4 mr-2" />Message {card.ownerName.split(' ')[0]}
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ))}

        {filteredCards.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#64748b]">
            <p>No businesses found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
}
